import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { BusinessProfileWizard } from '../components/onboarding/BusinessProfileWizard';
import { businessService } from '../services/businessService';
import proactiveDigestsService, { ProactiveRolePreset } from '../services/proactiveDigestsService';
import { supabase } from '../config/supabaseClient';
import { Tooltip } from 'react-tooltip';

type Destination = 'consultant' | 'ideas' | 'planner' | 'business_plan';

type OnboardingMode = 'quick' | 'full';

type BusinessSummary = {
  id: string;
  name: string;
  industry?: string | null;
  stage?: string | null;
};

type ActivationProgress = {
  businessContext: boolean;
  consultantOutput: boolean;
  savedArtifact: boolean;
};

const TrialOnboarding: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);

  const [mode, setMode] = useState<OnboardingMode | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  const [quickBusinessName, setQuickBusinessName] = useState('');
  const [quickIndustry, setQuickIndustry] = useState('');
  const [quickStage, setQuickStage] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  const [destination, setDestination] = useState<Destination>('consultant');
  const [rolePreset, setRolePreset] = useState<ProactiveRolePreset>('sales_led_smb');
  const [newsTopicsText, setNewsTopicsText] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [activation, setActivation] = useState<ActivationProgress>({
    businessContext: false,
    consultantOutput: false,
    savedArtifact: false,
  });

  const [digestPreviewId, setDigestPreviewId] = useState<string | null>(null);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [digestPreviewText, setDigestPreviewText] = useState<string | null>(null);

  const defaultBusiness = useMemo(() => {
    if (!businesses.length) return null;
    return businesses[0];
  }, [businesses]);

  const refreshBusinesses = async () => {
    const list = await businessService.list();
    const mapped: BusinessSummary[] = (list || []).map((b: any) => ({
      id: b.id,
      name: b.name,
      industry: b.industry ?? null,
      stage: b.stage ?? null,
    }));
    setBusinesses(mapped);
  };

  const refreshActivation = async (nextBusinesses?: BusinessSummary[]) => {
    if (!user?.id) return;

    const businessContext = (nextBusinesses || businesses).length > 0;

    const [chatsRes, ideasRes, legacyIdeasRes, plansRes] = await Promise.all([
      supabase.from('business_consultant_chats').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      supabase.from('business_ideas').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      supabase.from('ideas').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      supabase.from('business_plans').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
    ]);

    const consultantOutput = !chatsRes.error && (chatsRes.count || 0) > 0;
    const savedArtifact =
      (!ideasRes.error && (ideasRes.count || 0) > 0) ||
      (!legacyIdeasRes.error && (legacyIdeasRes.count || 0) > 0) ||
      (!plansRes.error && (plansRes.count || 0) > 0);

    setActivation({ businessContext, consultantOutput, savedArtifact });
  };

  useEffect(() => {
    const run = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        await refreshBusinesses();
        await refreshActivation();

        try {
          const email = localStorage.getItem('trial_checkout_email');
          if (email && email.trim().length > 0) {
          }
        } catch {}
      } catch (err) {
        console.error('Failed to load onboarding context:', err);
        toast.error('Failed to load onboarding details');
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [user?.id]);

  useEffect(() => {
    const onFocus = () => {
      void refreshBusinesses().then(() => refreshActivation());
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user?.id]);

  const shouldShowProfileStep = businesses.length === 0;

  const persistWizardDismissed = () => {
    if (!user?.id) return;
    try {
      const dismissedKey = `onboarding_dismissed_${user.id}`;
      localStorage.setItem(dismissedKey, 'true');
    } catch {}
  };

  const parseTopics = (raw: string): string[] => {
    return String(raw || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 5);
  };

  const buildSeedPrompt = () => {
    const b = defaultBusiness;
    const parts: string[] = [];
    if (b?.name) parts.push(`Business: ${b.name}`);
    if (b?.industry) parts.push(`Industry: ${b.industry}`);
    if (b?.stage) parts.push(`Stage: ${b.stage}`);

    const topics = parseTopics(newsTopicsText);
    if (topics.length > 0) parts.push(`News topics: ${topics.join(', ')}`);

    const context = parts.length > 0 ? parts.join('\n') + '\n\n' : '';
    return (
      context +
      'I have just started my trial. Give me a focused 7-day action plan with the 3 highest-impact moves I should make this week. Then ask me 3 short questions to refine the plan.'
    );
  };

  const continueToPreferences = () => {
    setMode((prev) => prev || 'quick');
  };

  const handleQuickCreate = async () => {
    if (!quickBusinessName.trim()) {
      toast.error('Enter your business name');
      return;
    }

    setQuickSubmitting(true);
    try {
      await businessService.create({
        name: quickBusinessName.trim(),
        ...(quickIndustry.trim() ? { industry: quickIndustry.trim() } : {}),
        ...(quickStage.trim() ? { stage: quickStage.trim() } : {}),
        is_default: true,
      } as any);

      persistWizardDismissed();
      const list = await businessService.list();
      const mapped: BusinessSummary[] = (list || []).map((b: any) => ({
        id: b.id,
        name: b.name,
        industry: b.industry ?? null,
        stage: b.stage ?? null,
      }));
      setBusinesses(mapped);
      await refreshActivation(mapped);
      toast.success('Saved. You can add more details later for better AI responses.');
      setMode('quick');
    } catch (err: any) {
      console.error('Quick setup failed:', err);
      toast.error(err?.message || 'Quick setup failed');
    } finally {
      setQuickSubmitting(false);
    }
  };

  const handleSavePreferencesAndContinue = async () => {
    setSavingPrefs(true);
    try {
      const topics = parseTopics(newsTopicsText);

      let timezone = 'UTC';
      try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      } catch {}

      await proactiveDigestsService.updateSettings({
        enabled: true,
        timezone,
        morning_enabled: true,
        morning_time: '08:00',
        lunch_enabled: false,
        lunch_time: '12:30',
        email_enabled: true,
        in_app_enabled: true,
        deal_alerts_enabled: true,
        news_topics: topics,
        role_preset: rolePreset,
      });

      setDigestPreviewLoading(true);
      const created = await proactiveDigestsService.createTestRun({ digest_type: 'morning' });
      const id = created?.data?.id ? String(created.data.id) : '';
      if (!id) {
        setDigestPreviewLoading(false);
        toast.success('Saved preferences');
        const nextPrompt = buildSeedPrompt();
        const q = new URLSearchParams({ prompt: nextPrompt }).toString();
        navigate(destination === 'consultant' ? `/business-consultant?${q}` : destination === 'ideas' ? '/idea-generator' : destination === 'planner' ? '/launchpad' : '/business-plan');
        return;
      }

      setDigestPreviewId(id);
      try {
        const run = await proactiveDigestsService.getRun(id);
        setDigestPreviewText(run?.data?.summary_text ? String(run.data.summary_text) : null);
      } catch {
        setDigestPreviewText(null);
      } finally {
        setDigestPreviewLoading(false);
      }
    } catch (err: any) {
      console.error('Failed to save onboarding preferences:', err);
      toast.error(err?.message || 'Failed to save onboarding preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const continueAfterDigestPreview = () => {
    const nextPrompt = buildSeedPrompt();
    const q = new URLSearchParams({ prompt: nextPrompt }).toString();
    navigate(destination === 'consultant' ? `/business-consultant?${q}` : destination === 'ideas' ? '/idea-generator' : destination === 'planner' ? '/launchpad' : '/business-plan');
  };

  const checklistItems: Array<{ key: keyof ActivationProgress; title: string; description: string }> = [
    { key: 'businessContext', title: 'Add business context', description: 'Quick setup or full business profile' },
    { key: 'consultantOutput', title: 'Get your first AI consultant output', description: 'Start a consultant conversation' },
    { key: 'savedArtifact', title: 'Save one artifact', description: 'Save an idea or create a plan' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Setting up your onboarding…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome — let’s get you value fast</h1>
          <p className="text-gray-600">
            The more you tell Explanatum, the better the AI performs. You can do a quick setup now, or complete the full Business Profile.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Your trial checklist</h2>
          <p className="text-gray-600 mb-6">Complete these 3 steps to get the most out of your trial.</p>

          <Tooltip id="trial-onboarding" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {checklistItems.map((it) => {
              const done = activation[it.key];
              return (
                <div key={it.key} className={`rounded-xl border p-5 ${done ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900" data-tooltip-id="trial-onboarding" data-tooltip-content={it.description}>{it.title}</div>
                      <div className="mt-1 text-sm text-gray-600">{it.description}</div>
                    </div>
                    <div className={`text-sm font-semibold ${done ? 'text-green-700' : 'text-gray-500'}`}>{done ? 'Done' : 'Next'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {shouldShowProfileStep && (
          <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Step 1: Business setup</h2>
            <p className="text-gray-600 mb-6">Choose how you want to set up your business context.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick setup (recommended if you’re in a rush)</h3>
                <p className="text-sm text-gray-600 mb-4">Just enough context to start. You can complete the full profile later.</p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business name</label>
                    <input
                      value={quickBusinessName}
                      onChange={(e) => setQuickBusinessName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g. Acme Ltd"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry (optional)</label>
                    <input
                      value={quickIndustry}
                      onChange={(e) => setQuickIndustry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="e.g. SaaS/Software"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stage (optional)</label>
                    <input
                      value={quickStage}
                      onChange={(e) => setQuickStage(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="idea / mvp / growth / mature"
                    />
                  </div>

                  <button
                    onClick={() => void handleQuickCreate()}
                    disabled={quickSubmitting}
                    className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {quickSubmitting ? 'Saving…' : 'Save quick setup'}
                  </button>

                  <button
                    onClick={() => continueToPreferences()}
                    className="w-full py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                  >
                    Skip business setup for now
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Full Business Profile Wizard</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Best results. The AI will use this context across the Business Consultant, plans, and tools.
                </p>

                <button
                  onClick={() => {
                    setMode('full');
                    setShowWizard(true);
                  }}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl"
                >
                  Start full setup
                </button>

                <p className="text-xs text-gray-500 mt-3">
                  Tip: Full setup takes ~2–4 minutes and improves AI responses significantly.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl border-2 border-indigo-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Step 2: Your AI + news preferences</h2>
          <p className="text-gray-600 mb-6">Choose what updates you want feeding into your Business AI Consultant.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role preset</label>
              <select
                value={rolePreset}
                onChange={(e) => setRolePreset(e.target.value as ProactiveRolePreset)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="solo_founder">Solo founder</option>
                <option value="sales_led_smb">Sales-led SMB</option>
                <option value="agency">Agency</option>
                <option value="ecom">E-commerce</option>
              </select>
              <p className="text-xs text-gray-500 mt-2">Used to tailor digests and AI priorities.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">News topics (comma-separated, max 5)</label>
              <input
                value={newsTopicsText}
                onChange={(e) => setNewsTopicsText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="e.g. AI in retail, UK SMEs, Premier League"
              />
              <p className="text-xs text-gray-500 mt-2">These topics will influence your updates and consultant context.</p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">What do you want to do first?</label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <button
                type="button"
                onClick={() => setDestination('consultant')}
                data-tooltip-id="trial-onboarding"
                data-tooltip-content="Fastest wow: get a concrete next-step plan from the AI consultant"
                className={`px-4 py-3 rounded-lg border text-sm font-medium ${destination === 'consultant' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
              >
                AI Consultant
              </button>
              <button
                type="button"
                onClick={() => setDestination('ideas')}
                data-tooltip-id="trial-onboarding"
                data-tooltip-content="Generate and save ideas you can revisit later"
                className={`px-4 py-3 rounded-lg border text-sm font-medium ${destination === 'ideas' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
              >
                Idea Generator
              </button>
              <button
                type="button"
                onClick={() => setDestination('planner')}
                data-tooltip-id="trial-onboarding"
                data-tooltip-content="Organize tasks and execution steps into a simple plan"
                className={`px-4 py-3 rounded-lg border text-sm font-medium ${destination === 'planner' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
              >
                Launchpad
              </button>
              <button
                type="button"
                onClick={() => setDestination('business_plan')}
                data-tooltip-id="trial-onboarding"
                data-tooltip-content="Create a full business plan from an idea"
                className={`px-4 py-3 rounded-lg border text-sm font-medium ${destination === 'business_plan' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
              >
                Business Plan
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => void handleSavePreferencesAndContinue()}
              disabled={savingPrefs || digestPreviewLoading || !!digestPreviewId}
              className="flex-1 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl disabled:opacity-60"
            >
              {savingPrefs ? 'Saving…' : 'Continue'}
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 py-4 bg-white border-2 border-gray-300 text-gray-700 text-lg font-semibold rounded-xl hover:bg-gray-50"
            >
              Do this later
            </button>
          </div>

          {digestPreviewId ? (
            <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-semibold text-gray-900">Preview: your morning digest</div>
                  <div className="mt-1 text-sm text-gray-600">This is what you’ll receive based on your settings.</div>
                </div>
                <a
                  href={`/digests/${digestPreviewId}`}
                  className="text-sm font-semibold text-blue-700 hover:underline"
                >
                  Open full view
                </a>
              </div>

              {digestPreviewLoading ? (
                <div className="mt-4 text-sm text-gray-600">Generating preview…</div>
              ) : digestPreviewText ? (
                <pre className="mt-4 whitespace-pre-wrap rounded-md bg-white p-4 text-sm text-gray-800 border border-gray-200">
                  {digestPreviewText}
                </pre>
              ) : (
                <div className="mt-4 text-sm text-gray-600">Preview generated. Open the full view for details.</div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => continueAfterDigestPreview()}
                  className="flex-1 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700"
                >
                  Continue
                </button>
                <button
                  onClick={() => {
                    setDigestPreviewId(null);
                    setDigestPreviewText(null);
                  }}
                  className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Adjust settings
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showWizard && (
        <BusinessProfileWizard
          onComplete={() => {
            setShowWizard(false);
            void refreshBusinesses().then(() => refreshActivation());
            toast.success('Business profile saved.');
          }}
          onSkip={() => {
            setShowWizard(false);
            persistWizardDismissed();
          }}
        />
      )}
    </div>
  );
};

export default TrialOnboarding;
